export type QuestionId =
  | 'industry'
  | 'teamSize'
  | 'challenges'
  | 'workMode'
  | 'currentTools'
  | 'complianceNeeds'
  | 'locationCount';

export interface Question {
  id: QuestionId;
  stepLabel: string;
  title: string;
  subtitle: string;
  options: { value: string; label: string; icon: string }[];
  multiSelect: boolean;
  maxSelect?: number;
  skippable?: boolean;
}

export interface Recommendation {
  product: string;
  tagline: string;
  icon: string;
  description: string;
  bullets: string[];
  slug: string;
}

export interface RecommendationOutcome {
  primary: Recommendation[];
  whyThisFits: string;
  expectedOutcomes: string[];
  planTier: 'Starter' | 'Growth' | 'Enterprise';
  planDescription: string;
  confidence: number;
  needCoverage: boolean;
  isBespoke?: boolean;
}

// ------------------------------------------------------------------
// Question Definitions
// ------------------------------------------------------------------

const QUESTION_DEFS: Record<QuestionId, Question> = {
  industry: {
    id: 'industry',
    stepLabel: 'Industry',
    title: 'What industry is your business in?',
    subtitle: 'This helps us understand your operational context.',
    options: [
      { value: 'logistics', label: 'Logistics / Fulfilment', icon: 'ri-truck-line' },
      { value: 'retail', label: 'Retail / E-commerce', icon: 'ri-store-2-line' },
      { value: 'manufacturing', label: 'Manufacturing', icon: 'ri-building-2-line' },
      { value: 'professional', label: 'Professional Services', icon: 'ri-briefcase-line' },
      { value: 'healthcare', label: 'Healthcare', icon: 'ri-hospital-line' },
      { value: 'fitness', label: 'Fitness / Wellness', icon: 'ri-heart-pulse-line' },
      { value: 'construction', label: 'Construction / Field Services', icon: 'ri-hammer-line' },
      { value: 'bespoke', label: 'Bespoke / Custom Solution', icon: 'ri-tools-line' },
      { value: 'other', label: 'Other', icon: 'ri-more-line' },
    ],
    multiSelect: false,
  },
  teamSize: {
    id: 'teamSize',
    stepLabel: 'Size',
    title: 'How many people are on your team?',
    subtitle: 'Team size helps us recommend the right scale of solution.',
    options: [
      { value: '1-10', label: '1-10 people', icon: 'ri-user-line' },
      { value: '11-50', label: '11-50 people', icon: 'ri-group-line' },
      { value: '51-200', label: '51-200 people', icon: 'ri-team-line' },
      { value: '200+', label: '200+ people', icon: 'ri-building-4-line' },
    ],
    multiSelect: false,
  },
  challenges: {
    id: 'challenges',
    stepLabel: 'Challenge',
    title: 'What are your biggest challenges?',
    subtitle: 'Select 1-2 pain points you need to solve first.',
    options: [
      { value: 'staff', label: 'Managing staff / productivity', icon: 'ri-team-line' },
      { value: 'operations', label: 'Tracking operations', icon: 'ri-task-line' },
      { value: 'reporting', label: 'Reporting & visibility', icon: 'ri-bar-chart-line' },
      { value: 'billing', label: 'Billing / revenue tracking', icon: 'ri-bill-line' },
      { value: 'performance', label: 'Performance management', icon: 'ri-award-line' },
      { value: 'marketing', label: 'Marketing / lead generation', icon: 'ri-megaphone-line' },
      { value: 'sales', label: 'Sales pipeline / CRM', icon: 'ri-exchange-dollar-line' },
      { value: 'workplace', label: 'Workplace services / internal ops', icon: 'ri-building-4-line' },
    ],
    multiSelect: true,
    maxSelect: 2,
  },
  workMode: {
    id: 'workMode',
    stepLabel: 'Work Mode',
    title: 'How does your team work?',
    subtitle: 'This determines the right deployment model.',
    options: [
      { value: 'office', label: 'Office / On-site', icon: 'ri-home-office-line' },
      { value: 'remote', label: 'Remote / Hybrid', icon: 'ri-global-line' },
      { value: 'field', label: 'Field / Mobile', icon: 'ri-map-pin-2-line' },
    ],
    multiSelect: false,
  },
  currentTools: {
    id: 'currentTools',
    stepLabel: 'Tools',
    title: 'What tools are you using today?',
    subtitle: 'Select all that apply, or skip this question.',
    options: [
      { value: 'excel', label: 'Excel / Spreadsheets', icon: 'ri-file-excel-line' },
      { value: 'whatsapp', label: 'WhatsApp / Messaging', icon: 'ri-whatsapp-line' },
      { value: 'erp', label: 'Existing ERP / Software', icon: 'ri-server-line' },
      { value: 'none', label: 'None / Paper-based', icon: 'ri-file-paper-line' },
    ],
    multiSelect: true,
    skippable: true,
  },
  complianceNeeds: {
    id: 'complianceNeeds',
    stepLabel: 'Compliance',
    title: 'Which compliance areas matter to your business?',
    subtitle: 'Select the areas you need to track and report on.',
    options: [
      { value: 'quality-audits', label: 'Quality Audits', icon: 'ri-shield-check-line' },
      { value: 'safety-inspections', label: 'Safety Inspections', icon: 'ri-alarm-warning-line' },
      { value: 'regulatory-reporting', label: 'Regulatory Reporting', icon: 'ri-file-list-3-line' },
      { value: 'all', label: 'All of the above', icon: 'ri-stack-line' },
    ],
    multiSelect: true,
    maxSelect: 2,
  },
  locationCount: {
    id: 'locationCount',
    stepLabel: 'Locations',
    title: 'How many locations does your team operate across?',
    subtitle: 'This helps us recommend the right scale of solution.',
    options: [
      { value: 'single', label: 'Single location', icon: 'ri-home-line' },
      { value: '2-5', label: '2 to 5 locations', icon: 'ri-building-2-line' },
      { value: '6+', label: '6 or more locations', icon: 'ri-building-4-line' },
    ],
    multiSelect: false,
  },
};

const BASE_QUESTION_ORDER: QuestionId[] = [
  'industry',
  'teamSize',
  'challenges',
  'workMode',
  'currentTools',
];

interface ConditionalInsertion {
  after: QuestionId;
  questionId: QuestionId;
  condition: (answers: Partial<Answers>) => boolean;
}

const CONDITIONAL_INSERTIONS: ConditionalInsertion[] = [
  {
    after: 'industry',
    questionId: 'complianceNeeds',
    condition: (a) =>
      a?.industry === 'healthcare' ||
      a?.industry === 'manufacturing' ||
      a?.industry === 'construction',
  },
  {
    after: 'workMode',
    questionId: 'locationCount',
    condition: (a) => a?.workMode === 'remote' || a?.workMode === 'field',
  },
];

export function getQuestions(answers: Partial<Answers> | undefined = {}): Question[] {
  const safeAnswers = answers || {};
  const result: Question[] = [];
  for (const qid of BASE_QUESTION_ORDER) {
    result.push(QUESTION_DEFS[qid]);
    for (const insertion of CONDITIONAL_INSERTIONS) {
      if (insertion.after === qid && insertion.condition(safeAnswers)) {
        result.push(QUESTION_DEFS[insertion.questionId]);
      }
    }
  }
  return result;
}

export function getTotalSteps(answers: Partial<Answers> | undefined = {}): number {
  return getQuestions(answers).length;
}

// ------------------------------------------------------------------
// Answers & Recommendations
// ------------------------------------------------------------------

export interface Answers {
  industry?: string;
  teamSize?: string;
  challenges?: string[];
  workMode?: string;
  currentTools?: string[];
  complianceNeeds?: string[];
  locationCount?: string;
}

const PRODUCT_CATALOG: Record<string, Recommendation> = {
  WorkTrack: {
    product: 'WorkTrack',
    tagline:
      'See everything your team is doing, in real time, wherever they work.',
    icon: 'ri-map-pin-user-line',
    description:
      'WorkTrack gives field and mobile teams a single, real-time view of what needs doing, where, and by whom. Supervisors assign tasks with precise GPS check-ins, and staff submit daily reports straight from their phones. No paper, no guesswork.',
    bullets: [
      'GPS-enabled task assignments with live location tracking',
      'Daily work reporting and digital timesheets',
      'Mobile-first interface built for field conditions',
      'Offline-first functionality that syncs when connected',
      'Supervisor dashboards for real-time team oversight',
    ],
    slug: 'worktrack',
  },
  HCM: {
    product: 'HCM',
    tagline: 'Manage your people from hire to exit, in one place.',
    icon: 'ri-user-settings-line',
    description:
      'HCM replaces scattered spreadsheets and manual paperwork with a clean, centralised employee database. It handles onboarding, leave requests, attendance tracking, and role management, all with audit trails and permission controls built in.',
    bullets: [
      'Centralised employee records and onboarding workflows',
      'Leave and attendance tracking with approval chains',
      'Role-based permissions and organisational hierarchy',
      'Automated reminders for policy and compliance dates',
      'Export-ready reports for payroll and audit needs',
    ],
    slug: 'hris',
  },
  PMS: {
    product: 'PMS',
    tagline:
      'Set targets, track performance, and build a culture of accountability.',
    icon: 'ri-bar-chart-grouped-line',
    description:
      'PMS turns performance reviews from a once-a-year event into an ongoing conversation. Managers set clear KPIs, track progress continuously, and run structured 360 feedback cycles, so teams always know how they are doing and what to improve.',
    bullets: [
      'KPI dashboards with real-time goal tracking',
      'Automated performance review scheduling and templates',
      '360 feedback collection from peers and managers',
      'Scorecard views for individual and team performance',
      'Trend analytics to spot rising or declining contributors',
    ],
    slug: 'pms',
  },
  OMS: {
    product: 'OMS',
    tagline:
      'Run your warehouse, manage your inventory, and serve your clients with precision.',
    icon: 'ri-shopping-cart-2-line',
    description:
      'OMS connects every stage of an order from intake to delivery. It tracks inventory across locations, syncs stock levels automatically, and gives customers a portal to check their order status, all while feeding live data to finance and operations teams.',
    bullets: [
      'Order intake through to fulfilment with status tracking',
      'Inventory sync across warehouses and retail locations',
      'Customer portal for self-service order visibility',
      'Automated alerts for low stock or delayed shipments',
      'Revenue and order analytics by channel and product',
    ],
    slug: 'oms',
  },
  'Quality Audit': {
    product: 'Quality Audit',
    tagline: 'Plan, execute, and close audits without the paperwork.',
    icon: 'ri-shield-check-line',
    description:
      'Quality Audit gives quality managers and compliance officers a structured digital system for every stage of the audit lifecycle. Build custom checklists, capture findings with severity ratings, track non-conformances through to closure, and generate scored compliance reports with full historical records.',
    bullets: [
      'Audit planning and scheduling by department or standard',
      'Custom checklist builder for any compliance framework',
      'Real-time findings capture with photos and severity ratings',
      'Non-conformance and corrective action tracking with escalation',
      'Scored compliance reports and full audit history',
    ],
    slug: 'quality-audit',
  },
  Accounting: {
    product: 'Accounting',
    tagline:
      'Invoice accurately, track every payment, and stop losing revenue to manual processes.',
    icon: 'ri-coins-line',
    description:
      'Accounting brings invoicing, payments, and revenue analytics into one place. Create branded invoices, track who has paid and who has not, and build custom financial reports without exporting data into a spreadsheet every week.',
    bullets: [
      'Invoice and payment tracking with status automation',
      'Revenue analytics by product, channel, and period',
      'Expense categorisation and approval workflows',
      'Recurring billing and subscription management',
      'Financial dashboards for cash flow and AR visibility',
    ],
    slug: 'finance',
  },
  'Learning & Development': {
    product: 'Learning & Development',
    tagline:
      'Build the skills your business needs inside the platform your team already uses.',
    icon: 'ri-graduation-cap-line',
    description:
      'Learning & Development turns scattered training into structured, trackable programmes. Build courses, assign them to teams or individuals, and monitor completion rates and assessment scores. Ideal for onboarding, compliance, and upskilling.',
    bullets: [
      'Course builder with assignments, quizzes, and videos',
      'Progress tracking at individual and team level',
      'Certification management with expiry reminders',
      'Onboarding pathways for new hires',
      'Competency gap analysis and training recommendations',
    ],
    slug: 'learn',
  },
  'RemoteOps Hub': {
    product: 'RemoteOps Hub',
    tagline: 'Command center for distributed operations.',
    icon: 'ri-remote-control-line',
    description:
      'RemoteOps Hub is the command centre for businesses with multiple branches or remote teams. It pulls operational data from every location into a single dashboard, enabling real-time decision-making without travelling to every site.',
    bullets: [
      'Centralised branch control with live operational feeds',
      'Remote team dashboards for attendance and task status',
      'Cross-location reporting with comparative analytics',
      'Location-based alerts and escalation routing',
      'Unified communication layer across all branches',
    ],
    slug: 'remoteops-hub',
  },
  Admin: {
    product: 'Admin',
    tagline: 'Bring structure and full visibility to your internal operations.',
    icon: 'ri-shield-user-line',
    description:
      'Admin is the backbone of the StackMap platform, giving IT and operations leads full control over who can access what. It manages user provisioning, role definitions, audit logs, and system-wide configuration from one secure console.',
    bullets: [
      'System-wide configuration and policy management',
      'User provisioning with bulk import and SSO support',
      'Granular role-based access across all modules',
      'Audit logging for compliance and security review',
      'Custom field and workflow configuration tools',
    ],
    slug: 'admin',
  },
  'Full Stack': {
    product: 'Full Stack (All Modules)',
    tagline: 'Complete operational platform for growing enterprises.',
    icon: 'ri-stack-line',
    description:
      'Full Stack unites every StackMap module into a single enterprise platform. Data flows freely between HR, operations, finance, and inventory, so there is one source of truth, fewer manual handoffs, and a foundation for custom workflows as you scale.',
    bullets: [
      'All modules included with unified data layer',
      'Priority support and dedicated account management',
      'Custom workflow automation across departments',
      'Advanced integrations with third-party ERP and CRM tools',
      'Enterprise-grade security, audit, and compliance controls',
    ],
    slug: '',
  },
  'Growth & Marketing': {
    product: 'Growth & Marketing',
    tagline: 'Drive awareness, generate demand, and execute campaigns that convert prospects into loyal customers.',
    icon: 'ri-megaphone-line',
    description:
      'Growth & Marketing gives marketing teams a unified platform to plan campaigns, create and schedule content, capture leads, run email and SMS marketing, segment audiences, and measure performance. With AI-assisted content suggestions and native Sales & CRM integration, your marketing engine runs smarter and faster.',
    bullets: [
      'Campaign management across email, social, and web channels',
      'Lead capture forms that feed directly into the CRM pipeline',
      'Audience segmentation with behavioural targeting',
      'Marketing analytics with real-time ROI dashboards',
      'AI-assisted content suggestions and content calendar',
    ],
    slug: 'growth-marketing',
  },
  'Sales & CRM': {
    product: 'Sales & CRM',
    tagline: 'Build stronger customer relationships and manage your sales pipeline with confidence.',
    icon: 'ri-exchange-dollar-line',
    description:
      'Sales & CRM gives sales teams a complete system to manage leads, track opportunities through the pipeline, maintain contact and account records, log sales activities, generate quotations and proposals, and forecast revenue with confidence. Native integration with Growth & Marketing ensures every qualified lead flows seamlessly into the sales process.',
    bullets: [
      'Drag-and-drop pipeline with deal stage tracking',
      'Lead scoring and automated assignment from marketing forms',
      'Contact and account management with full communication history',
      'Quotation and proposal generation with pricing tables',
      'Revenue forecasting with stage-weighted probability models',
    ],
    slug: 'sales-crm',
  },
  'Workplace Services': {
    product: 'Workplace Services',
    tagline: 'Create a more organised, efficient, and employee-friendly workplace.',
    icon: 'ri-building-4-line',
    description:
      'Workplace Services transforms how organisations handle internal service delivery. From facility maintenance requests and meeting room bookings to visitor management, asset allocation, and helpdesk ticketing, every workplace service runs through a structured digital workflow. Employees submit requests through a self-service portal, managers approve with context, and facilities teams track everything with SLA monitoring.',
    bullets: [
      'Self-service portal for all internal service requests',
      'Facility and maintenance tracking with SLA monitoring',
      'Meeting room booking with calendar integration',
      'Visitor management with pre-registration and host notifications',
      'Asset requests, allocation, and return tracking',
    ],
    slug: 'workplace-services',
  },
  'Get Fit': {
    product: 'Get Fit',
    tagline: 'Smart fitness management for gyms, studios, and wellness businesses.',
    icon: 'ri-heart-pulse-line',
    description:
      'Get Fit is a comprehensive fitness management platform that helps gyms, fitness studios, and wellness providers deliver exceptional member experiences while streamlining daily operations. From digital membership management and class booking to trainer tools, progress tracking, and multi-branch analytics, it unifies every aspect of running a modern fitness business.',
    bullets: [
      'Digital membership management with subscription and payment tracking',
      'Class booking and scheduling with trainer coordination',
      'Progress tracking with body measurements, photos, and activity monitoring',
      'Fitness challenges and rewards to drive member engagement',
      'Multi-branch gym administration with real-time analytics',
    ],
    slug: 'get-fit',
  },
};

export function getRecommendations(answers: Answers): RecommendationOutcome {
  const {
    industry,
    teamSize,
    challenges = [],
    workMode,
    currentTools = [],
    complianceNeeds = [],
    locationCount,
  } = answers;

  // Bespoke route: custom build request
  if (industry === 'bespoke') {
    return {
      primary: [],
      whyThisFits:
        'Your operational needs are unique and do not fit a standard off-the-shelf configuration. Our product engineering team works directly with you to design, build, and deploy a custom StackMap solution tailored to your exact workflows, integrations, and scale requirements.',
      expectedOutcomes: [
        'A solution purpose-built for your specific operational model',
        'Direct collaboration with our engineering and design team',
        'Flexible roadmap and priority support aligned to your business goals',
      ],
      planTier: 'Enterprise',
      planDescription:
        'Custom-scoped engagement with a dedicated build team, bespoke integrations, and full platform access.',
      confidence: 1,
      needCoverage: true,
      isBespoke: true,
    };
  }

  // ------------------------------------------------------------------
  // Scoring Engine
  // ------------------------------------------------------------------
  const W_CHALLENGE = 5;
  const W_INDUSTRY = 3;
  const W_WORKMODE = 2;
  const W_COMPLIANCE = 2;
  const W_LOCATION = 1;
  const W_TOOL = 1;

  // Challenge → products
  const challengeScores: Record<string, Record<string, number>> = {
    staff: { HCM: W_CHALLENGE, 'Get Fit': W_CHALLENGE },
    operations: { WorkTrack: W_CHALLENGE, OMS: W_CHALLENGE, 'Quality Audit': W_CHALLENGE * 0.4 },
    reporting: { PMS: W_CHALLENGE, OMS: W_CHALLENGE * 0.5 },
    billing: { Accounting: W_CHALLENGE, OMS: W_CHALLENGE * 0.4 },
    performance: { PMS: W_CHALLENGE, HCM: W_CHALLENGE * 0.5 },
    marketing: { 'Growth & Marketing': W_CHALLENGE, 'Sales & CRM': W_CHALLENGE * 0.4 },
    sales: { 'Sales & CRM': W_CHALLENGE, 'Growth & Marketing': W_CHALLENGE * 0.4 },
    workplace: { 'Workplace Services': W_CHALLENGE, Admin: W_CHALLENGE * 0.5 },
  };

  // Industry → products
  const industryScores: Record<string, Record<string, number>> = {
    logistics: { OMS: W_INDUSTRY, 'Quality Audit': W_INDUSTRY, WorkTrack: W_INDUSTRY },
    retail: { OMS: W_INDUSTRY, Accounting: W_INDUSTRY, 'Growth & Marketing': W_INDUSTRY * 0.5 },
    manufacturing: { 'Quality Audit': W_INDUSTRY, WorkTrack: W_INDUSTRY, HCM: W_INDUSTRY * 0.5 },
    construction: { WorkTrack: W_INDUSTRY, 'RemoteOps Hub': W_INDUSTRY },
    healthcare: { HCM: W_INDUSTRY, 'Learning & Development': W_INDUSTRY, 'Quality Audit': W_INDUSTRY, 'Get Fit': W_INDUSTRY },
    fitness: { 'Get Fit': W_INDUSTRY, HCM: W_INDUSTRY * 0.5, 'Growth & Marketing': W_INDUSTRY * 0.3, Accounting: W_INDUSTRY * 0.3 },
    professional: { PMS: W_INDUSTRY, Accounting: W_INDUSTRY, 'Sales & CRM': W_INDUSTRY * 0.6, WorkTrack: W_INDUSTRY * 0.5 },
  };

  // Work mode → products
  const workModeScores: Record<string, Record<string, number>> = {
    field: { WorkTrack: W_WORKMODE, 'RemoteOps Hub': W_WORKMODE, 'Sales & CRM': W_WORKMODE * 0.3 },
    remote: { 'RemoteOps Hub': W_WORKMODE, WorkTrack: W_WORKMODE * 0.5, HCM: W_WORKMODE * 0.5, 'Growth & Marketing': W_WORKMODE * 0.3 },
    office: { HCM: W_WORKMODE, PMS: W_WORKMODE, 'Workplace Services': W_WORKMODE * 0.6, OMS: W_WORKMODE * 0.5, 'Growth & Marketing': W_WORKMODE * 0.3, 'Get Fit': W_WORKMODE * 0.5 },
  };

  // Compliance → products
  const complianceScores: Record<string, Record<string, number>> = {
    'quality-audits': { 'Quality Audit': W_COMPLIANCE },
    'safety-inspections': { 'Quality Audit': W_COMPLIANCE },
    'regulatory-reporting': { Admin: W_COMPLIANCE, 'Quality Audit': W_COMPLIANCE * 0.5 },
    all: { 'Quality Audit': W_COMPLIANCE, Admin: W_COMPLIANCE },
  };

  // Location → products
  const locationScores: Record<string, Record<string, number>> = {
    '2-5': { 'RemoteOps Hub': W_LOCATION },
    '6+': { 'RemoteOps Hub': W_LOCATION, Admin: W_LOCATION },
  };

  // Tools → products
  const toolScores: Record<string, Record<string, number>> = {
    excel: { WorkTrack: W_TOOL, OMS: W_TOOL },
    whatsapp: { WorkTrack: W_TOOL, OMS: W_TOOL },
    erp: { Admin: W_TOOL, 'RemoteOps Hub': W_TOOL },
    none: { WorkTrack: W_TOOL, HCM: W_TOOL },
  };

  // Challenge → covering products (for coverage-aware selection)
  const challengeProductMapping: Record<string, string[]> = {
    staff: ['HCM', 'Get Fit'],
    operations: ['WorkTrack', 'OMS', 'Quality Audit'],
    reporting: ['PMS', 'OMS'],
    billing: ['Accounting', 'OMS'],
    performance: ['PMS', 'HCM'],
    marketing: ['Growth & Marketing', 'Sales & CRM'],
    sales: ['Sales & CRM', 'Growth & Marketing'],
    workplace: ['Workplace Services', 'Admin'],
  };

  // Accumulate scores
  const scores: Record<string, number> = {};
  function addScores(mapping: Record<string, number> | undefined) {
    if (!mapping) return;
    for (const [product, score] of Object.entries(mapping)) {
      scores[product] = (scores[product] || 0) + score;
    }
  }

  for (const challenge of challenges) addScores(challengeScores[challenge]);
  if (industry) addScores(industryScores[industry]);
  if (workMode) addScores(workModeScores[workMode]);
  for (const need of complianceNeeds) addScores(complianceScores[need]);
  if (locationCount) addScores(locationScores[locationCount]);
  for (const tool of currentTools) addScores(toolScores[tool]);

  // Rank products by score
  const ranked = Object.entries(scores)
    .map(([product, score]) => ({ product, score }))
    .sort((a, b) => b.score - a.score)
    .filter((e) => e.score >= 1);

  if (ranked.length === 0) {
    ranked.push({ product: 'WorkTrack', score: 2 }, { product: 'HCM', score: 1 });
  }

  // ------------------------------------------------------------------
  // Coverage-Aware Selection
  // ------------------------------------------------------------------
  let primary: Recommendation[];

  const isLargeTeam = teamSize === '200+';
  const hasBroadNeeds = challenges.length >= 2 && ranked.length >= 4 && ranked[3].score >= 3;

  const top2CoverAll = (() => {
    if (ranked.length < 2) return false;
    const topProducts = new Set([ranked[0].product, ranked[1].product]);
    return challenges.every((ch) =>
      (challengeProductMapping[ch] || []).some((p) => topProducts.has(p))
    );
  })();

  if (isLargeTeam && hasBroadNeeds && !top2CoverAll) {
    primary = [PRODUCT_CATALOG['Full Stack']];
  } else {
    const selected: typeof ranked = [];
    const covered = new Set<string>();

    // Always include the top-scoring product
    if (ranked.length > 0) {
      selected.push(ranked[0]);
      for (const ch of challenges) {
        if ((challengeProductMapping[ch] || []).includes(ranked[0].product)) {
          covered.add(ch);
        }
      }
    }

    let pool = ranked.slice(1);

    // Greedily add products that cover uncovered challenges
    while (covered.size < challenges.length && pool.length > 0 && selected.length < 3) {
      let bestIdx = 0;
      let bestCoverage = 0;
      for (let i = 0; i < pool.length; i++) {
        const coverage = challenges.filter(
          (ch) =>
            !covered.has(ch) &&
            (challengeProductMapping[ch] || []).includes(pool[i].product)
        ).length;
        if (coverage > bestCoverage || (coverage === bestCoverage && pool[i].score > pool[bestIdx].score)) {
          bestCoverage = coverage;
          bestIdx = i;
        }
      }

      if (bestCoverage === 0) {
        if (selected.length < 3) selected.push(pool[0]);
        break;
      }

      selected.push(pool[bestIdx]);
      for (const ch of challenges) {
        if ((challengeProductMapping[ch] || []).includes(pool[bestIdx].product)) {
          covered.add(ch);
        }
      }
      pool = pool.filter((_, i) => i !== bestIdx);
    }

    // If we have room and a close runner-up exists, add it
    if (selected.length === 1 && pool.length > 0 && selected[0].score - pool[0].score < 4) {
      selected.push(pool[0]);
    }

    primary = selected.map((s) => PRODUCT_CATALOG[s.product]).filter(Boolean);
  }

  if (primary.length === 0) {
    primary = [PRODUCT_CATALOG['WorkTrack']];
  }

  // ------------------------------------------------------------------
  // Need Coverage
  // ------------------------------------------------------------------
  const primaryNames = new Set(primary.map((p) => p.product));
  let coveredChallenges = 0;
  for (const ch of challenges) {
    if ((challengeProductMapping[ch] || []).some((p) => primaryNames.has(p))) {
      coveredChallenges++;
    }
  }
  const needCoverage = challenges.length === 0 || coveredChallenges === challenges.length;

  // ------------------------------------------------------------------
  // Confidence (based on actual score ratios)
  // ------------------------------------------------------------------
  const maxPossibleScore =
    challenges.length * W_CHALLENGE +
    (industry ? W_INDUSTRY : 0) +
    (workMode ? W_WORKMODE : 0) +
    complianceNeeds.length * W_COMPLIANCE +
    (locationCount ? W_LOCATION : 0) +
    currentTools.length * W_TOOL;

  const topScore = ranked[0]?.score || 0;
  const maxScoreForUser = Math.max(maxPossibleScore, topScore);

  let baseConfidence = maxScoreForUser > 0 ? topScore / maxScoreForUser : 0.65;

  if (primary.length === 1 && primary[0].product === 'Full Stack') {
    baseConfidence = 0.95;
  } else if (primary.length === 1) {
    baseConfidence = Math.max(baseConfidence, 0.78);
  } else if (primary.length === 2) {
    baseConfidence = Math.max(baseConfidence - 0.03, 0.65);
  } else if (primary.length >= 3) {
    baseConfidence = Math.max(baseConfidence - 0.08, 0.55);
  }

  if (needCoverage) baseConfidence += 0.05;
  const confidence = Math.min(0.98, Math.round(baseConfidence * 100) / 100);

  // ------------------------------------------------------------------
  // Why This Fits (specific to recommended products)
  // ------------------------------------------------------------------
  const challengeMap: Record<string, string> = {
    staff: 'managing staff and productivity',
    operations: 'tracking day-to-day operations',
    reporting: 'reporting and operational visibility',
    billing: 'billing and revenue tracking',
    performance: 'performance management',
    marketing: 'marketing and lead generation',
    sales: 'sales pipeline and CRM',
    workplace: 'workplace services and internal operations',
  };

  const challengeText =
    challenges.length > 0
      ? challenges.map((c) => challengeMap[c] || c).join(' and ')
      : 'improving operational efficiency';

  const teamText = teamSize ? `a ${teamSize} person team` : 'your team';
  const modeText =
    workMode === 'field'
      ? 'working in the field'
      : workMode === 'remote'
        ? 'working remotely'
        : 'based in an office';

  const productReasons = primary
    .map((p) => {
      const reasons: string[] = [];
      for (const ch of challenges) {
        if ((challengeProductMapping[ch] || []).includes(p.product)) {
          reasons.push(challengeMap[ch] || ch);
        }
      }
      if (industry && industryScores[industry]?.[p.product]) {
        const label = industry === 'professional' ? 'professional services' : industry;
        reasons.push(`your ${label} industry`);
      }
      if (workMode && workModeScores[workMode]?.[p.product]) {
        reasons.push(`your ${modeText} setup`);
      }
      if (reasons.length > 0) {
        return `${p.product} directly addresses ${reasons.slice(0, 2).join(' and ')}`;
      }
      return `${p.product} complements your core needs`;
    })
    .join('. ');

  const whyThisFits = `Based on ${teamText} ${modeText} with a focus on ${challengeText}, we recommend ${primary.map((p) => p.product).join(' and ')}. ${productReasons}. These modules address your core pain points and scale with your business as it grows.`;

  // ------------------------------------------------------------------
  // Expected Outcomes (product-specific, not generic)
  // ------------------------------------------------------------------
  const outcomeSet = new Set<string>();
  const productOutcomeMap: Record<string, string[]> = {
    WorkTrack: [
      'Real-time visibility into field and mobile team activity',
      'Digital task assignments with GPS tracking and proof of work',
    ],
    HCM: [
      'Centralised people management from onboarding to offboarding',
      'Automated leave, attendance, and role tracking',
    ],
    PMS: [
      'Continuous performance tracking with KPI dashboards',
      'Structured feedback cycles and goal alignment',
    ],
    OMS: [
      'End-to-end order and inventory visibility across locations as a standalone platform',
      'Automated stock alerts and fulfilment tracking with real-time multi-warehouse sync',
      'Self-service merchant and client portal reducing inquiry volume',
    ],
    'Quality Audit': [
      'Structured audit workflows with scored compliance reports',
      'Non-conformance tracking with photo evidence and escalation',
    ],
    Accounting: [
      'Streamlined invoicing with automated payment reminders',
      'Real-time revenue and cash flow visibility',
    ],
    'Learning & Development': [
      'Structured training programmes with completion tracking',
      'Competency gap analysis and certification management',
    ],
    'RemoteOps Hub': [
      'Unified operational control across all branch locations',
      'Cross-location reporting with real-time dashboards',
    ],
    Admin: [
      'Granular role-based access and user provisioning',
      'Complete audit trails for compliance and security review',
    ],
    'Growth & Marketing': [
      'Unified campaign management across email, social, and web channels',
      'Automated lead capture and qualification feeding directly into CRM',
    ],
    'Sales & CRM': [
      'Full pipeline visibility with drag-and-drop deal stage tracking',
      'Accurate revenue forecasting with stage-weighted probability models',
    ],
    'Workplace Services': [
      'Self-service portal reducing internal request response times',
      'Real-time SLA monitoring across all workplace service categories',
    ],
    'Get Fit': [
      'Unified member management with digital membership tracking',
      'Streamlined class booking and trainer coordination',
    ],
  };

  for (const p of primary) {
    for (const o of productOutcomeMap[p.product] || []) {
      outcomeSet.add(o);
    }
  }

  // Fallback challenge-driven outcomes if product ones are sparse
  if (outcomeSet.size < 2) {
    if (challenges.includes('staff') || challenges.includes('operations')) {
      outcomeSet.add('Improved visibility into daily operations and team activity');
    }
    if (challenges.includes('reporting') || challenges.includes('performance')) {
      outcomeSet.add('Faster reporting with real-time dashboards and automated insights');
    }
    if (challenges.includes('billing')) {
      outcomeSet.add('Streamlined billing and revenue tracking with fewer errors');
    }
    if (challenges.includes('marketing') || challenges.includes('sales')) {
      outcomeSet.add('Higher conversion rates with seamless marketing-to-sales handoff');
    }
    if (challenges.includes('workplace')) {
      outcomeSet.add('Faster internal service resolution with structured digital workflows');
    }
  }

  if (complianceNeeds.length > 0) {
    outcomeSet.add('Streamlined compliance tracking with audit-ready reports');
  }
  if (locationCount === '2-5' || locationCount === '6+') {
    outcomeSet.add('Unified visibility and control across all locations');
  }
  if (workMode === 'field' || workMode === 'remote') {
    outcomeSet.add('Centralised control for distributed teams and field operations');
  }

  if (outcomeSet.size === 0) {
    outcomeSet.add('Improved visibility into daily operations');
    outcomeSet.add('Better accountability across your team');
  }

  const outcomes = Array.from(outcomeSet).slice(0, 3);

  // ------------------------------------------------------------------
  // Plan Tier
  // ------------------------------------------------------------------
  let planTier: 'Starter' | 'Growth' | 'Enterprise';
  let planDescription: string;

  if (!teamSize || teamSize === '1-10') {
    planTier = 'Starter';
    planDescription =
      'Core modules for small teams getting started with structured operations.';
  } else if (teamSize === '11-50') {
    planTier = 'Growth';
    planDescription =
      'Expanded features with multi-user access and deeper reporting for growing teams.';
  } else {
    planTier = 'Enterprise';
    planDescription =
      'Full platform access with advanced integrations, custom workflows, and priority support.';
  }

  return {
    primary,
    whyThisFits,
    expectedOutcomes: outcomes,
    planTier,
    planDescription,
    confidence,
    needCoverage,
  };
}